import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { DesktopDatePicker, DesktopTimePicker } from '@mui/x-date-pickers';
import PatientTravelApi from 'api/patient/PatientTravelApi';
import NotificationManager from 'common/notifications/NotificationManager';
import { DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import { ROLE_SITE_FINANCIAL_COORDINATOR, ROLE_TRAVEL_COORDINATOR } from 'constants/userRoles';
import { userHasAccessAs } from 'services/auth';
import { onRequestError } from 'services/handlers';

import { NSPP_TRAVEL } from '../../../../../../../services/i18n/namespaces';
import {
  TR_BOOKED,
  TR_DENIED,
  travelServiceTypes,
  travelStatuses
} from '../../EncountersSection/TravelSection/travelConstants';

export default function MainSection({
  encounterInfo,
  values,
  errors,
  actions,
  navigateBack,
  isEdit,
  ssuPatientId,
  travelRequestId,
  readOnly = false
}) {
  const { t } = useTranslation();
  const { setFieldValue, handleCheckboxGroupChange } = actions;
  const { status, visitDate, visitStartTime, visitEndTime, requestedServices } = values;

  const allowApproveOrDeny = userHasAccessAs([ROLE_SITE_FINANCIAL_COORDINATOR, ROLE_TRAVEL_COORDINATOR]);

  return (
    <>
      <Grid
        container
        spacing={3}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="nowrap"
        sx={{ padding: '0 12px' }}
      >
        <Grid size="auto">
          <Typography variant="h6" sx={{ maxWidth: 500 }}>
            {encounterInfo?.originalEncounterName || encounterInfo?.encounterName}
          </Typography>
        </Grid>
        <Grid size="grow">
          <TextField
            disabled={!isEdit || readOnly}
            name="status"
            value={status}
            onChange={statusOnChange}
            sx={{ width: 200 }}
            select
            label={t(`${NSPP_TRAVEL}:statuses.status`)}
            variant="standard"
          >
            {travelStatuses.map(e => (
              <MenuItem
                key={e.id}
                value={e.id}
                disabled={[TR_DENIED, TR_BOOKED].includes(e.id) ? !allowApproveOrDeny : false}
              >
                {t(`${NSPP_TRAVEL}:statuses.${e.name}`)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size="auto">
          <IconButton
            onClick={function() {
              navigateBack();
            }}
          >
            <CloseIcon data-testid="CloseIcon" />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems="flex-start" sx={{ padding: '0 12px', pt: 2 }}>
        <Grid size="auto">
          <DesktopDatePicker
            disabled={readOnly}
            disablePast={true}
            name="visitDate"
            value={visitDate}
            timezone="system"
            onChange={function(date) {
              setFieldValue('visitDate', date);
            }}
            label={t(`${NSPP_TRAVEL}:travelRequests.visitDate`)}
            format={DD_SLASH_MMM_SLASH_YYYY}
            views={['year', 'month', 'day']}
            slotProps={{
              textField: {
                variant: 'standard',
                required: true,
                fullWidth: true,
                error: Boolean(errors.visitDate),
                helperText: errors.visitDate
              }
            }}
            required
          />
        </Grid>
        <Grid size="auto">
          <DesktopTimePicker
            disabled={readOnly}
            name="visitStartTime"
            label={t(`${NSPP_TRAVEL}:travelRequests.visitStartTime`)}
            value={visitStartTime}
            timezone="system"
            onChange={function(value) {
              return setFieldValue('visitStartTime', value);
            }}
            slotProps={{
              textField: {
                variant: 'standard',
                required: true,
                fullWidth: true,
                error: Boolean(errors.visitStartTime),
                helperText: errors.visitStartTime
              }
            }}
            required
          />
        </Grid>
        <Grid size="auto">
          <DesktopTimePicker
            disabled={readOnly}
            name="visitEndTime"
            label={t(`${NSPP_TRAVEL}:travelRequests.visitEndTime`)}
            value={visitEndTime}
            timezone="system"
            onChange={function(value) {
              return setFieldValue('visitEndTime', value);
            }}
            slotProps={{
              textField: {
                variant: 'standard',
                fullWidth: true,
                error: Boolean(errors.visitEndTime),
                helperText: errors.visitEndTime
              }
            }}
          />
        </Grid>
        <Grid size="grow"></Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center" sx={{ padding: '0 12px', pt: 3 }}>
        <Grid size={12}>
          <Typography variant="subtitle2">{t(`${NSPP_TRAVEL}:services.requestedServices`)}</Typography>
        </Grid>
        <Grid size={12}>
          <FormControl
            disabled={readOnly}
            component="fieldset"
            variant="standard"
            error={Boolean(errors.requestedServices)}
            required
          >
            <FormGroup row>
              {travelServiceTypes.map(function({ id, name }) {
                return (
                  <FormControlLabel
                    key={id}
                    sx={{ margin: 0 }}
                    control={
                      <Checkbox
                        checked={requestedServices.includes(id)}
                        onChange={handleCheckboxGroupChange}
                        value={id}
                        name="requestedServices"
                      />
                    }
                    label={t(`${NSPP_TRAVEL}:services.${name}`)}
                  />
                );
              })}
            </FormGroup>
            {Boolean(errors.requestedServices) && <FormHelperText>{errors.requestedServices}</FormHelperText>}
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
  function statusOnChange({ target }) {
    const status = target.value;
    PatientTravelApi.updateStatus(ssuPatientId, travelRequestId, status)
      .then(function() {
        setFieldValue('status', status);
        NotificationManager.success('Travel Request status was changed successfully');
      })
      .catch(onRequestError);
  }
}
